import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Star, Phone, ArrowRight, MapPin, Clock, Shield } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactCTASection from "@/components/ContactCTASection";
import TestimonialsSection from "@/components/TestimonialsSection";
import AreasServedSection from "@/components/AreasServedSection";
import { useState } from "react";
import EstimateDialog from "@/components/EstimateDialog";
import { useNavigate } from "react-router-dom";

interface CityLandingPageProps {
  city: string;
  county: string;
  zipCodes: string[];
  nearbyAreas: string[];
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalPath: string;
  heroSubtitle: string;
  localContent: string;
  faqs: { question: string; answer: string }[];
}

const CityLandingPage = ({
  city,
  county,
  zipCodes,
  nearbyAreas,
  seoTitle,
  seoDescription,
  seoKeywords,
  canonicalPath,
  heroSubtitle,
  localContent,
  faqs,
}: CityLandingPageProps) => {
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const navigate = useNavigate();
  const baseUrl = "https://organizebydesignmichigan.com";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}${canonicalPath}`,
    name: `Organize by Design - ${city}, Michigan`,
    description: seoDescription,
    url: `${baseUrl}${canonicalPath}`,
    telephone: "+1-616-727-0343",
    email: "hello@organizebydesignmichigan.com",
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: city,
      addressRegion: "MI",
      addressCountry: "US",
    },
    areaServed: [
      { "@type": "City", name: city, addressRegion: "Michigan" },
      ...nearbyAreas.map((area) => ({
        "@type": "City",
        name: area,
        addressRegion: "Michigan",
      })),
    ],
    serviceType: [
      "Home Organization",
      "Professional Cleaning",
      "ADHD Organizing",
      "Real Estate Staging",
      "Deep Cleaning",
      "Move-In Move-Out Cleaning",
      "Decluttering Services",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5",
      reviewCount: "47",
      bestRating: "5",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "16:00",
      },
    ],
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  const services = [
    {
      title: "Home Organization",
      desc: `Professional home organizing in ${city}. Closets, kitchens, garages, basements — custom systems designed for your lifestyle.`,
      link: "/organizing",
    },
    {
      title: "House Cleaning",
      desc: `Residential cleaning services in ${city}. Weekly, bi-weekly, deep cleaning, and move-in/move-out cleaning.`,
      link: "/cleaning",
    },
    {
      title: "ADHD-Friendly Organizing",
      desc: `Neurodivergent-friendly organizing systems in ${city}. Visual cues, simple routines, low-overwhelm solutions.`,
      link: "/organizing",
    },
    {
      title: "Real Estate Staging",
      desc: `Pre-listing decluttering and staging in ${city}. Help your home sell faster and for more.`,
      link: "/organizing",
    },
    {
      title: "Same-Day Service",
      desc: `Emergency organizing and cleaning available same-day in ${city}. Last-minute guests? We're here.`,
      link: "/same-day-organizing",
    },
    {
      title: "Commercial Cleaning",
      desc: `Office, retail, and commercial cleaning in ${city}. After-hours available. Custom contracts.`,
      link: "/cleaning",
    },
  ];

  return (
    <>
      <SEOHelmet
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`${baseUrl}${canonicalPath}`}
        structuredData={structuredData}
      />

      <Header onBookingClick={() => setShowEstimateDialog(true)} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                ))}
              </div>
              <span className="text-sm font-medium text-primary">
                5-Star Rated • Licensed & Insured
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Professional Organizing & Cleaning in{" "}
              <span className="italic text-primary">{city}, Michigan</span>
            </h1>

            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {heroSubtitle}
            </p>

            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <Button
                onClick={() => setShowEstimateDialog(true)}
                size="lg"
                className="luxury-gradient text-primary-foreground font-semibold px-8 py-6 text-lg rounded-full"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-6 rounded-full" asChild>
                <a href="tel:+16167270343">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 616.727.0343
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {city}, {county} County</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> Same-Day Available</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> Insured & Bonded</span>
            </div>
          </div>
        </div>
      </section>

      {/* Local content block */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-serif font-light text-center mb-6">
              Why {city} Homeowners Choose Organize by Design
            </h2>
            <p className="text-muted-foreground text-center leading-relaxed">{localContent}</p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-center mb-4">
            Organizing & Cleaning Services in {city}
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Full-service home organization and professional cleaning for {city} residents and businesses.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, idx) => (
              <Card
                key={idx}
                className="border-border hover:border-primary/30 transition-colors cursor-pointer group"
                onClick={() => navigate(service.link)}
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-serif font-medium mb-3 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">{service.desc}</p>
                  <span className="text-primary text-sm font-medium">Learn more →</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zip codes & nearby */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-light mb-6">
            Serving {city} &amp; Surrounding Areas
          </h2>
          <p className="text-muted-foreground mb-4">
            We serve all {city} zip codes: <strong>{zipCodes.join(", ")}</strong>
          </p>
          <p className="text-muted-foreground mb-8">
            Also serving nearby: {nearbyAreas.join(" • ")}
          </p>
          <Button onClick={() => setShowEstimateDialog(true)} size="lg">
            Book in {city}
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-center mb-12">
            {city} Organizing & Cleaning FAQ
          </h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-border">
                  <AccordionTrigger className="text-left hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
            />
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <AreasServedSection onBookingClick={() => setShowEstimateDialog(true)} />
      <ContactCTASection onBookingClick={() => setShowEstimateDialog(true)} />
      <Footer onBookingClick={() => setShowEstimateDialog(true)} />

      <EstimateDialog open={showEstimateDialog} onOpenChange={setShowEstimateDialog} />
    </>
  );
};

export default CityLandingPage;
